import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// ✅ Configuration API backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [isLoading, setIsLoading] = useState(true);
  const [mustChangePassword, setMustChangePasswordState] = useState(false);

  // La session peut avoir été sauvegardée dans localStorage (rememberMe) ou
  // sessionStorage (sinon) lors du login : on regarde où se trouve
  // effectivement le token avant de lire quoi que ce soit.
  const getAuthStorage = () => {
    return localStorage.getItem(TOKEN_KEY) ? localStorage : sessionStorage;
  };

  const isTokenExpired = (storage) => {
    const store = storage || getAuthStorage();
    const expiry = store.getItem(TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    return Date.now() > parseInt(expiry);
  };

  useEffect(() => {
    const initAuth = async () => {
      const storage = getAuthStorage();
      const token = storage.getItem(TOKEN_KEY);
      const userData = storage.getItem(USER_DATA_KEY);

      if (token && userData && !isTokenExpired(storage)) {
        try {
          const user = JSON.parse(userData);
          setIsLoggedIn(true);
          setUsername(user.username);
          // Normalisation en minuscules : le backend renvoie "ADMIN"/"USER",
          // le reste du front compare en minuscules ("admin").
          setUserRole((user.userRole || 'user').toLowerCase());
          setMustChangePasswordState(Boolean(user.mustChangePassword));
        } catch (error) {
          console.error('Erreur lors de la restauration de la session', error);
          logout();
        }
      } else {
        logout();
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // ✅ LOGIN AVEC VRAIE API BACKEND
  const login = async (email, password, rememberMe = false) => {
    try {
      // ✅ Appel à la VRAIE API backend
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Si la réponse n'est pas OK, le backend a rejeté le login
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.message || 'Email ou mot de passe incorrect'
        };
      }

      const data = await response.json();

      // Extraire les données de la réponse.
      // ⚠️ Le backend (AuthResponse) renvoie les champs à plat dans "data"
      // (token, id, email, username, role) et non dans un sous-objet "user".
      const authData = data.data || data;
      const token = authData.token;
      const user = authData.user || authData;

      // Calculer l'expiration (1 heure par défaut)
      const expiresIn = authData.expiresIn || 3600;
      const expiryTime = Date.now() + (expiresIn * 1000);

      // Sauvegarder selon rememberMe
      const storage = rememberMe ? localStorage : sessionStorage;

      // Normalisation en minuscules : le backend renvoie "ADMIN"/"USER".
      const normalizedRole = (user.role || user.userRole || 'user').toLowerCase();
      // Le backend renvoie ce champ en snake_case ("must_change_password")
      const requiresPasswordChange = Boolean(authData.must_change_password || authData.mustChangePassword);

      storage.setItem(TOKEN_KEY, token);
      storage.setItem(USER_DATA_KEY, JSON.stringify({
        username: user.username || user.name || email.split('@')[0],
        userRole: normalizedRole,
        email: user.email || email,
        mustChangePassword: requiresPasswordChange
      }));
      storage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      if (rememberMe) {
        localStorage.setItem(REFRESH_TOKEN_KEY, authData.refreshToken || '');
      }

      // Mettre à jour l'état
      setIsLoggedIn(true);
      setUsername(user.username || user.name || email.split('@')[0]);
      setUserRole(normalizedRole);
      setMustChangePasswordState(requiresPasswordChange);

      return { success: true, mustChangePassword: requiresPasswordChange };
    } catch (error) {
      console.error('Erreur de connexion', error);
      return {
        success: false,
        error: 'Impossible de se connecter au serveur. Vérifiez votre connexion.'
      };
    }
  };

  // ✅ REGISTER AVEC VRAIE API BACKEND - CORRIGÉ
  const register = async (name, email, password, rememberMe = false) => {
    try {
      // ✅ CORRECTION : Le backend attend "username" et non "name"
      // On génère un username à partir du nom (retire espaces, met en minuscules)
      const username = name.toLowerCase().replace(/\s+/g, '');

      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: username,  // ✅ AJOUTÉ : username obligatoire
          email: email,
          password: password
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.message || 'Erreur lors de l\'inscription'
        };
      }

      // Après inscription, connecter automatiquement
      return await login(email, password, rememberMe);
    } catch (error) {
      console.error('Erreur d\'inscription', error);
      return {
        success: false,
        error: 'Impossible de se connecter au serveur'
      };
    }
  };

  const logout = () => {
    // Nettoyer tout le stockage
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    sessionStorage.clear();

    // Réinitialiser l'état
    setIsLoggedIn(false);
    setUsername('');
    setUserRole('user');
    setMustChangePasswordState(false);
  };

  // À appeler après un changement de mot de passe réussi, pour lever la
  // redirection forcée vers l'écran de changement de mot de passe.
  const clearMustChangePassword = () => {
    setMustChangePasswordState(false);
    const stored = localStorage.getItem(USER_DATA_KEY) ? localStorage : sessionStorage;
    const userData = stored.getItem(USER_DATA_KEY);
    if (userData) {
      try {
        const user = JSON.parse(userData);
        stored.setItem(USER_DATA_KEY, JSON.stringify({ ...user, mustChangePassword: false }));
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la session', error);
      }
    }
  };

  // À appeler après une modification du profil : le backend renvoie un
  // nouveau token (l'email est le sujet du JWT) et le nouveau nom.
  const updateSession = ({ token, username: newUsername, email: newEmail }) => {
    const storage = getAuthStorage();
    try {
      if (token) storage.setItem(TOKEN_KEY, token);
      const userData = storage.getItem(USER_DATA_KEY);
      const user = userData ? JSON.parse(userData) : {};
      storage.setItem(USER_DATA_KEY, JSON.stringify({
        ...user,
        username: newUsername || user.username,
        email: newEmail || user.email
      }));
      // Même durée de session que lors de la connexion
      storage.setItem(TOKEN_EXPIRY_KEY, (Date.now() + 3600 * 1000).toString());
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la session', error);
    }
    if (newUsername) setUsername(newUsername);
  };

  const getToken = () => {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);
  };

  const authenticatedFetchInternal = async (url, options = {}) => {
    const token = getToken();

    if (!token) {
      logout();
      throw new Error('Session expirée');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };

    return fetch(url, { ...options, headers });
  };

  const value = {
    isLoggedIn,
    username,
    userRole,
    isLoading,
    mustChangePassword,
    login,
    register,
    logout,
    getToken,
    clearMustChangePassword,
    updateSession,
    authenticatedFetch: authenticatedFetchInternal
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ✅ Export de authenticatedFetch pour les services
export const authenticatedFetch = async (url, options = {}) => {
  const TOKEN_KEY = 'auth_token';
  const token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);

  if (!token) {
    throw new Error('Non authentifié');
  }

  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  return fetch(url, { ...options, headers });
};